import { debounce } from '@solid-primitives/scheduled'
import { createSignal } from 'solid-js'
import type { SearchCategory } from './categories'
import type { FilterValues } from './filters'

const RECENT_KEY = 'global_search_recent'
const MAX_RECENT = 6

const readRecent = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
  } catch {
    return []
  }
}

export function useGlobalSearch() {
  const [isOpen, setIsOpen] = createSignal(false)
  const [input, setInputRaw] = createSignal('')
  const [query, setQuery] = createSignal('')
  const [category, setCategory] = createSignal<SearchCategory | undefined>()
  const [recent, setRecent] = createSignal<string[]>(readRecent())

  // Per-category filters, kept while the app is open so switching categories doesn't lose them
  const [filters, setFilters] = createSignal<Partial<Record<SearchCategory, FilterValues>>>({})
  // Whether the filter panel is showing (it only exists while a category is selected)
  const [showFilters, setShowFilters] = createSignal(true)

  const categoryFilters = () => {
    const c = category()
    return c ? (filters()[c] ?? {}) : {}
  }
  const setFilter = (key: string, value: string | string[]) => {
    const c = category()
    if (!c) return
    setFilters({ ...filters(), [c]: { ...(filters()[c] ?? {}), [key]: value } })
  }
  const clearFilters = () => {
    const c = category()
    if (c) setFilters({ ...filters(), [c]: {} })
  }

  const commitQuery = debounce((value: string) => setQuery(value.trim()), 250)

  const setInput = (value: string) => {
    setInputRaw(value)
    commitQuery(value)
  }

  const open = (opts?: { category?: SearchCategory; query?: string }) => {
    if (opts?.category !== undefined) setCategory(opts.category)
    if (opts?.query !== undefined) {
      setInputRaw(opts.query)
      setQuery(opts.query)
    }
    setIsOpen(true)
  }

  const close = () => setIsOpen(false)

  const remember = (value: string) => {
    const q = value.trim()
    if (!q) return
    const next = [q, ...recent().filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, MAX_RECENT)
    setRecent(next)
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next))
    } catch {}
  }

  const clearRecent = () => {
    setRecent([])
    try {
      localStorage.removeItem(RECENT_KEY)
    } catch {}
  }

  return {
    isOpen,
    setIsOpen,
    open,
    close,
    input,
    setInput,
    query,
    category,
    setCategory,
    recent,
    remember,
    clearRecent,
    categoryFilters,
    setFilter,
    clearFilters,
    showFilters,
    setShowFilters,
  }
}
